'use client'

import { useEffect } from 'react'

/**
 * Scroll-in reveals for the whole site, without an animation library.
 *
 * An element opts in with `data-rv`; its value is how much of it has to be on
 * screen before it plays (0-1, default 0.25). CSS (styles/reveal.css) holds
 * it hidden until then; this sets `data-rv-in` and the CSS entrance runs.
 * `data-rv-repeat` plays it again each time the element comes back into view.
 *
 * The homepage sections used to do this with framer-motion's whileInView,
 * which put ~50 KB of JavaScript in front of the first paint on mobile. This
 * is one shared IntersectionObserver per threshold, plus a MutationObserver
 * for sections that render later (after a fetch, or a client navigation).
 */
const DEFAULT_AMOUNT = 0.25

export default function RevealObserver() {
  useEffect(() => {
    const show = (el) => el.setAttribute('data-rv-in', '')

    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('[data-rv]').forEach(show)
      return undefined
    }

    const observers = new Map()
    const observerOf = new WeakMap()

    const onEntries = (entries, io) => {
      const amount = observers.get(io)
      for (const entry of entries) {
        const el = entry.target
        // An element taller than the screen can never reach a large
        // `amount`, so filling most of the viewport counts as well.
        const fillsView = entry.rootBounds && entry.intersectionRect.height >= entry.rootBounds.height * 0.6
        if (entry.isIntersecting && (entry.intersectionRatio >= amount - 0.001 || fillsView)) {
          show(el)
          if (!el.hasAttribute('data-rv-repeat')) {
            io.unobserve(el)
            observerOf.delete(el)
          }
        } else if (!entry.isIntersecting && el.hasAttribute('data-rv-repeat')) {
          el.removeAttribute('data-rv-in')
        }
      }
    }

    const observe = (el) => {
      if (observerOf.has(el) || el.hasAttribute('data-rv-in')) return
      const parsed = parseFloat(el.getAttribute('data-rv'))
      const amount = Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : DEFAULT_AMOUNT
      let io = [...observers.entries()].find(([, a]) => a === amount)?.[0]
      if (!io) {
        // Steps below the target too, so fillsView is re-checked as a tall
        // element scrolls in.
        const steps = [0, 0.1, 0.2, 0.3, 0.4, 0.5].filter((t) => t < amount)
        io = new IntersectionObserver(onEntries, { threshold: [...steps, amount] })
        observers.set(io, amount)
      }
      observerOf.set(el, io)
      io.observe(el)
    }

    const forget = (el) => {
      const io = observerOf.get(el)
      if (io) io.unobserve(el)
      observerOf.delete(el)
    }

    const each = (node, fn) => {
      if (node.nodeType !== 1) return
      if (node.hasAttribute('data-rv')) fn(node)
      node.querySelectorAll('[data-rv]').forEach(fn)
    }

    document.querySelectorAll('[data-rv]').forEach(observe)

    const mutations = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => each(node, observe))
        record.removedNodes.forEach((node) => each(node, forget))
      }
    })
    mutations.observe(document.body, { childList: true, subtree: true })

    return () => {
      mutations.disconnect()
      observers.forEach((_, io) => io.disconnect())
    }
  }, [])

  return null
}
